import { useTranslation } from 'react-i18next'

import { Box, Divider, Stack, SxProps, Theme, Typography } from '@mui/material'

import responsiveHook from '@hooks/useResponsive'

import { TLGTypography } from '@components/TLGTypography'

import { scaleUpTransition } from '@utils/styles'

import {
  containerSx,
  contentGridSx,
  discoverPerfectTechMatchWithEaseLeftSideContentSx,
  discoverPerfectTechMatchWithEaseLeftSideWrapperSx,
  discoverPerfectTechMatchWithEaseRightSideImageSx,
  discoverPerfectTechMatchWithEaseRightSideWrapperSx,
  discoverSolutionTrustedProvidersLeftSideContentSx,
  discoverSolutionTrustedProvidersLeftSideWrapperSx,
  discoverSolutionTrustedProvidersRightSideImageSx,
  discoverSolutionTrustedProvidersRightSideWrapperSx,
  dividerWrapperSx,
  secondDividerWrapperSx,
  takeControlTechJourneyWithConfidenceLeftSideImageSx,
  takeControlTechJourneyWithConfidenceLeftSideWrapperSx,
  takeControlTechJourneyWithConfidenceRightSideContentSx,
  takeControlTechJourneyWithConfidenceRightSideWrapperSx,
  titleSx,
} from './styles'

import { BulletPoints } from '../BulletPoints'

const Content1Section = () => {
  const { t } = useTranslation()

  const { ifDesktop } = responsiveHook.useResponsive()

  return (
    <Box data-testid="features-content-1-section" sx={containerSx}>
      <Box
        display="grid"
        gridTemplateColumns="repeat(12, 1fr)"
        sx={contentGridSx}
      >
        <Box
          gridColumn={{ xs: 'span 12', lg: 'span 6' }}
          sx={discoverPerfectTechMatchWithEaseLeftSideWrapperSx}
        >
          <Stack sx={discoverPerfectTechMatchWithEaseLeftSideContentSx}>
            <TLGTypography
              responsiveVariant={{ xs: 'h5', md: 'h4' }}
              sx={titleSx}
            >
              {t('Discover your perfect tech match with ease ')}
            </TLGTypography>
            <Typography variant="body1">
              {t(
                'Our platform empowers you to conduct a precise search for the technology, solutions, services, partners, and brands you need. Effortlessly refine your search based on budget, geography, preferred payment structures, and specific requirements. Say goodbye to sifting through irrelevant options and hello to a carefully curated selection of technology partners that align perfectly with your needs.'
              )}
            </Typography>

            <BulletPoints
              list={[
                t(
                  'Our Google-like search bar allows you to input your specific needs, enabling personalized recommendations tailored to you.'
                ),
                t(
                  'Advanced algorithms analyze your requirements to connect you with technology partners perfectly aligned with your needs.'
                ),
                t(
                  'Rest assured that the technology partners you engage with are highly compatible, increasing the likelihood of successful partnerships.'
                ),
              ]}
            />
          </Stack>
        </Box>

        <Box
          gridColumn={{ xs: 'span 12', lg: 'span 6' }}
          sx={discoverPerfectTechMatchWithEaseRightSideWrapperSx}
        >
          <Box
            alt={t(
              'Tech Customer my matches and tech vendor profile pages screenshot'
            )}
            loading="lazy"
            component="img"
            src="/images/it-customer-my-matches-and-tech-vendor-profile-pages.webp"
            sx={
              {
                ...discoverPerfectTechMatchWithEaseRightSideImageSx,
                ...ifDesktop(scaleUpTransition),
              } as SxProps<Theme>
            }
          />
        </Box>

        <Box gridColumn="span 12" sx={dividerWrapperSx}>
          <Divider />
        </Box>

        <Box
          gridColumn={{ xs: 'span 12', lg: 'span 6' }}
          sx={takeControlTechJourneyWithConfidenceLeftSideWrapperSx}
        >
          <Box
            alt={t('Solution Details Page')}
            loading="lazy"
            component="img"
            src="/images/features-take-control-tech-journey-with-confidence.webp"
            sx={
              {
                ...takeControlTechJourneyWithConfidenceLeftSideImageSx,
                ...ifDesktop(scaleUpTransition),
              } as SxProps<Theme>
            }
          />
        </Box>

        <Box
          gridColumn={{ xs: 'span 12', lg: 'span 6' }}
          sx={takeControlTechJourneyWithConfidenceRightSideWrapperSx}
        >
          <Stack sx={takeControlTechJourneyWithConfidenceRightSideContentSx}>
            <TLGTypography
              responsiveVariant={{ xs: 'h5', md: 'h4' }}
              sx={titleSx}
            >
              {t('Take control of your tech journey with confidence')}
            </TLGTypography>
            <Typography variant="body1">
              {t(
                'Make empowered decisions and maintain privacy and security throughout your tech procurement process, remaining incognito so you can choose who to engage with and when. '
              )}
            </Typography>

            <BulletPoints
              list={[
                t(
                  'Take charge of your tech buying journey by accepting or declining matches, so you engage only with partners that align with your goals and requirements.'
                ),
                t(
                  "Enjoy the flexibility of exploring potential solutions and partners privately until you're ready to reveal your interest, ensuring privacy."
                ),
                t(
                  'Experience unparalleled efficiency in your tech procurement process as you engage in interactions that are meaningful and relevant to your needs, saving time and resources.'
                ),
              ]}
            />
          </Stack>
        </Box>

        <Box gridColumn="span 12" sx={secondDividerWrapperSx}>
          <Divider />
        </Box>

        <Box
          gridColumn={{ xs: 'span 12', lg: 'span 6' }}
          sx={discoverSolutionTrustedProvidersLeftSideWrapperSx}
        >
          <Stack sx={discoverSolutionTrustedProvidersLeftSideContentSx}>
            <TLGTypography
              responsiveVariant={{ xs: 'h5', md: 'h4' }}
              sx={titleSx}
            >
              {t('Discover cutting-edge solutions from trusted providers')}
            </TLGTypography>
            <Typography variant="body1">
              {t(
                'Unlock a vast catalog of cutting-edge technology solutions across industries and use cases. Connect with a curated network of reputable and verified technology partners.'
              )}
            </Typography>

            <BulletPoints
              list={[
                t(
                  'Our precise matchmaking capabilities help you navigate the catalog swiftly, allowing you to discover solutions that align with your needs and requirements.'
                ),
                t(
                  'Connect with a curated network of reputable technology partners who have established a proven track record for delivering quality solutions and services.'
                ),
                t(
                  'Stay ahead of the curve by exploring the latest technological advancements and innovations, facilitated by our extensive solution catalog and trusted vendor network.'
                ),
              ]}
            />
          </Stack>
        </Box>

        <Box
          gridColumn={{ xs: 'span 12', lg: 'span 6' }}
          sx={discoverSolutionTrustedProvidersRightSideWrapperSx}
        >
          <Box
            alt={t('Tech Vendor Profile and Saved Searches Pages')}
            loading="lazy"
            component="img"
            src="/images/features-discover-solution-trusted-providers.webp"
            sx={{
              ...discoverSolutionTrustedProvidersRightSideImageSx,
              ...ifDesktop(scaleUpTransition),
            }}
          />
        </Box>
      </Box>
    </Box>
  )
}

export { Content1Section }
